import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PencilSimple, Check, X, Plus, ArrowCounterClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { renderMarkdownLinks } from "@/lib/markdownLinks";

/**
 * RecipeSummaryCard — the "About this recipe" section on a recipe page.
 *
 * • Read-only card for viewers.
 * • Pencil icon toggles inline edit for the owner / admin.
 * • Up to 500 words; live word counter; character-cap safety net at 3 500
 *   so a runaway paste can't wreck layout.
 * • If there's no summary yet and the viewer can edit, a subtle "Add a
 *   summary" prompt lets them start one from scratch.
 */
export default function RecipeSummaryCard({ recipe, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(recipe?.summary || "");
  const [saving, setSaving] = useState(false);

  // Keep the draft in sync when the parent hands us a fresh recipe
  // (e.g. after a successful save that resets the incoming prop).
  useEffect(() => { setDraft(recipe?.summary || ""); }, [recipe?.summary]);

  const wordCount = useMemo(() => {
    const t = (draft || "").trim();
    return t ? t.split(/\s+/).length : 0;
  }, [draft]);

  const overLimit = wordCount > 500;

  const save = async () => {
    if (overLimit) {
      toast.error("Summary is over the 500-word limit — trim it down.");
      return;
    }
    setSaving(true);
    try {
      const finalText = (draft || "").trim();
      await api.patch(`/recipes/${recipe.recipe_id}`, { summary: finalText });
      // Refetch so we hydrate the freshly-archived `summary_prior` field
      // and any server-side normalisation of the summary text.
      try {
        const { data: fresh } = await api.get(`/recipes/${recipe.recipe_id}`);
        onSaved?.(fresh?.summary ?? finalText, { refetched: fresh });
      } catch (_) {
        onSaved?.(finalText);
      }
      toast.success("Summary saved.");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(recipe?.summary || "");
    setEditing(false);
  };

  const restore = async () => {
    if (!window.confirm("Restore the original AI-drafted summary? Your rewritten version will be replaced.")) return;
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/summary/restore`);
      onSaved?.(data?.summary || "", { restored: true });
      toast.success("Original summary restored.");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const hasSummary = Boolean((recipe?.summary || "").trim());
  const hasPrior = Boolean((recipe?.summary_prior || "").trim());

  // Nothing to render for viewers without a summary.
  if (!hasSummary && !canEdit) return null;

  return (
    <div
      className="rounded-2xl bg-rf-amber/10 border border-rf-amber/30 px-5 py-4 my-2 relative"
      data-testid="recipe-summary"
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <p className="text-[11px] font-black uppercase tracking-wide text-rf-orange">
          About this recipe
        </p>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="recipe-summary-edit"
            aria-label="Edit summary"
            title="Rewrite in your own voice (up to 500 words)"
            className="text-rf-ink-soft hover:text-rf-orange transition-colors flex-shrink-0"
          >
            <PencilSimple size={16} weight="duotone" />
          </button>
        )}
      </div>

      {!editing && hasSummary && (
        <>
          <div
            className="text-rf-ink leading-relaxed [&>p]:mb-3 [&>p:last-child]:mb-0"
            data-testid="recipe-summary-text"
          >
            {renderMarkdownLinks(recipe.summary, {
              paragraphClass: "text-rf-ink leading-relaxed",
              linkClass: "text-rf-orange font-bold underline hover:text-rf-ink",
            })}
          </div>
          {/* Restore original — visible only to editors when a prior
              AI-drafted summary is on file. One click swaps back and
              clears the archive so a future edit re-arms it. */}
          {canEdit && hasPrior && (
            <button
              type="button"
              onClick={restore}
              data-testid="recipe-summary-restore"
              className="mt-3 text-[11px] font-bold text-rf-orange hover:text-rf-ink inline-flex items-center gap-1"
              title="Swap back to the AI-drafted summary you started with"
            >
              <ArrowCounterClockwise size={12} weight="bold" /> Restore original AI summary
            </button>
          )}
        </>
      )}

      {!editing && !hasSummary && canEdit && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="recipe-summary-add"
          className="text-rf-orange hover:text-rf-ink text-sm font-bold inline-flex items-center gap-1"
        >
          <Plus size={14} weight="bold" /> Add a summary (up to 500 words)
        </button>
      )}

      {editing && (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            maxLength={3500}
            placeholder="What is this recipe? Where does it come from? Who taught you to cook it? Up to 500 words."
            data-testid="recipe-summary-textarea"
            className="w-full rounded-lg border border-rf-ink/15 px-3 py-2 text-rf-ink focus:outline-none focus:border-rf-amber bg-white leading-relaxed"
            autoFocus
          />
          <p className="text-[11px] text-rf-ink-soft mt-1.5 leading-snug">
            Tip: wrap hyperlinks like{" "}
            <code className="font-mono bg-rf-cream-alt px-1 rounded">[Kitchen Help: julienne](/kitchen-help/julienne)</code>{" "}
            — internal links (starting with <code>/</code>) open in the same tab, full URLs open in a new tab.
          </p>
          <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
            <p
              className={`text-xs font-bold ${overLimit ? "text-red-600" : "text-rf-ink-soft"}`}
              data-testid="recipe-summary-wordcount"
            >
              {wordCount} / 500 words
              {overLimit && " — please trim"}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                data-testid="recipe-summary-cancel"
                className="btn-outline-ink !py-1.5 !px-3 text-xs disabled:opacity-50"
              >
                <X size={14} /> Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || overLimit}
                data-testid="recipe-summary-save"
                className="btn-amber !py-1.5 !px-3 text-xs disabled:opacity-50"
              >
                <Check size={14} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
